import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Megaphone, Globe, MousePointer, Calendar, Tag, ExternalLink,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdCreativePreview } from './AdCreativePreview';

interface ClientOriginCardProps {
  client: {
    id?: string;
    ad_platform?: string;
    ad_campaign_id?: string;
    ad_adset_id?: string;
    ad_ad_id?: string;
    ad_ad_name?: string;
    ad_adset_name?: string;
    ad_attribution_method?: string;
    ad_attributed_at?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
    fbclid?: string;
    gclid?: string;
    organization_id?: string;
  };
}

const METHOD_LABELS: Record<string, string> = {
  lead_form: 'Lead Form',
  utm_match: 'UTM Match',
  fbclid: 'Facebook Click ID',
  gclid: 'Google Click ID',
  leadgen_retroactive: 'Leadgen Retroattivo',
  manual_assignment: 'Assegnazione Manuale',
};

function PropertyRow({ icon: Icon, label, value, onClick }: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <div
      className={`flex items-center gap-2 py-1.5 px-2 min-h-[28px] ${onClick ? 'cursor-pointer hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded' : ''}`}
      onClick={onClick}
    >
      <Icon className="h-3.5 w-3.5 text-[#787774] dark:text-gray-500 flex-shrink-0" />
      <span className="text-xs text-[#787774] dark:text-gray-400 w-[100px] flex-shrink-0">{label}</span>
      <span className="text-sm text-[#37352F] dark:text-white truncate">{value || '-'}</span>
    </div>
  );
}

export function ClientOriginCard({ client }: ClientOriginCardProps) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [campaignName, setCampaignName] = useState<string | null>(null);
  const [campaignDbId, setCampaignDbId] = useState<string | null>(null);

  const hasOriginData = client.ad_platform || client.utm_source || client.fbclid || client.gclid;
  if (!hasOriginData) return null;

  useEffect(() => {
    if (client.ad_campaign_id && client.organization_id) {
      supabase
        .from('ad_campaigns')
        .select('id, name')
        .eq('organization_id', client.organization_id)
        .eq('external_id', client.ad_campaign_id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            setCampaignName(data.name);
            setCampaignDbId(data.id);
          }
        });
    }
  }, [client.ad_campaign_id, client.organization_id]);

  // Riga di proprietà (redesign pannello 13/08): label "Origine" + sunto come
  // valore, dettagli (campagna, UTM, click id) nell'espansione al click.
  const summary = client.ad_platform
    ? client.ad_platform === 'meta' ? 'Meta Ads' : 'Google Ads'
    : client.utm_source || (client.fbclid ? 'Facebook Click' : client.gclid ? 'Google Click' : null);

  return (
    <div className="space-y-1">
      <div
        className="group flex min-h-[30px] cursor-pointer items-center rounded px-1.5 py-0.5 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06]"
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); } }}
      >
        <div className="flex w-32 shrink-0 items-center gap-1.5 text-xs font-medium text-[#787774] dark:text-gray-400">
          <Megaphone className="h-3 w-3" strokeWidth={1.5} />
          <span>Origine</span>
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] text-[#37352F] dark:text-white">
          {client.ad_platform ? (
            <span className={`rounded px-1.5 py-0.5 text-[11px] ${
              client.ad_platform === 'meta' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            }`}>
              {summary}
            </span>
          ) : (
            <span className="truncate">{summary}</span>
          )}
          {client.utm_campaign && client.ad_platform && (
            <span className="truncate text-[12px] text-[rgba(55,53,47,0.5)] dark:text-gray-400">· {client.utm_campaign}</span>
          )}
          {expanded ? <ChevronDown className="h-3 w-3 flex-shrink-0 text-[#787774]" /> : <ChevronRight className="h-3 w-3 flex-shrink-0 text-[#787774]" />}
        </div>
      </div>

      {expanded && (
        <div className="space-y-0.5 ml-1">
          {/* Creativita' dell'annuncio: immagine dalla memoria, testo chiesto a
              Meta al primo sguardo e poi memorizzato (vedi AdCreativePreview). */}
          {client.ad_platform === 'meta' && client.ad_ad_id && client.organization_id && client.id && (
            <div className="mb-1.5 px-1">
              <AdCreativePreview
                leadId={client.id}
                adId={client.ad_ad_id}
                organizationId={client.organization_id}
                adName={client.ad_ad_name}
              />
            </div>
          )}

          {/* Campaign */}
          {(campaignName || client.ad_campaign_id) && (
            <PropertyRow
              icon={Megaphone}
              label="Campagna"
              value={
                <span className="flex items-center gap-1">
                  {campaignName || client.ad_campaign_id}
                  {campaignDbId && (
                    <ExternalLink
                      className="w-3 h-3 text-[#0f6cbd] cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/marketing/campaigns/${campaignDbId}`);
                      }}
                    />
                  )}
                </span>
              }
              onClick={campaignDbId ? () => navigate(`/marketing/campaigns/${campaignDbId}`) : undefined}
            />
          )}

          {/* Adset */}
          {client.ad_adset_name && (
            <PropertyRow
              icon={Megaphone}
              label="Gruppo ins."
              value={client.ad_adset_name}
            />
          )}

          {/* Ad / Creative */}
          {(client.ad_ad_name || client.ad_ad_id) && (
            <PropertyRow
              icon={Megaphone}
              label="Inserzione"
              value={
                <span className="flex items-center gap-1">
                  {client.ad_ad_name || client.ad_ad_id}
                  {client.ad_ad_id && (
                    <ExternalLink
                      className="w-3 h-3 text-[#0f6cbd] cursor-pointer flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(
                          `https://www.facebook.com/adsmanager/manage/ads?selected_ad_ids=${client.ad_ad_id}`,
                          '_blank'
                        );
                      }}
                    />
                  )}
                </span>
              }
            />
          )}

          {/* Attribution method */}
          {client.ad_attribution_method && (
            <PropertyRow
              icon={Tag}
              label="Metodo"
              value={METHOD_LABELS[client.ad_attribution_method] || client.ad_attribution_method}
            />
          )}

          {/* Attribution date */}
          {client.ad_attributed_at && (
            <PropertyRow
              icon={Calendar}
              label="Data attribuz."
              value={new Date(client.ad_attributed_at).toLocaleDateString('it-IT', {
                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            />
          )}

          {/* UTM params */}
          {client.utm_source && (
            <PropertyRow icon={Globe} label="UTM Source" value={client.utm_source} />
          )}
          {client.utm_medium && (
            <PropertyRow icon={Globe} label="UTM Medium" value={client.utm_medium} />
          )}
          {client.utm_campaign && (
            <PropertyRow icon={Globe} label="UTM Campaign" value={client.utm_campaign} />
          )}
          {client.utm_content && (
            <PropertyRow icon={Globe} label="UTM Content" value={client.utm_content} />
          )}
          {client.utm_term && (
            <PropertyRow icon={Globe} label="UTM Term" value={client.utm_term} />
          )}

          {/* Click IDs */}
          {client.fbclid && (
            <PropertyRow icon={MousePointer} label="Facebook Click" value={`${client.fbclid.substring(0, 20)}...`} />
          )}
          {client.gclid && (
            <PropertyRow icon={MousePointer} label="Google Click" value={`${client.gclid.substring(0, 20)}...`} />
          )}
        </div>
      )}
    </div>
  );
}
